import { useId, useRef } from 'react';
import { cn } from '@doccareplus/shared/lib/utils';

export interface OtpInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  length?: number;
  hint?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  /** Optional native single-field mirror for assistive technology and form integrations. */
  nativeInputLabel?: string;
}

/** A controlled, paste- and autofill-aware one-time-code input. */
export function OtpInput({
  label,
  value,
  onChange,
  length = 6,
  hint,
  error,
  disabled,
  className,
  nativeInputLabel,
}: OtpInputProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const generatedId = useId();
  const feedbackId = `${generatedId}-feedback`;
  const normalizedValue = value.replace(/\D/g, '').slice(0, length);
  const digits = Array.from({ length }, (_, index) => normalizedValue[index] ?? '');

  const focusDigit = (index: number) => {
    refs.current[Math.max(0, Math.min(index, length - 1))]?.focus();
  };

  const commit = (nextValue: string, focusIndex?: number) => {
    onChange(nextValue.replace(/\D/g, '').slice(0, length));
    if (focusIndex !== undefined) focusDigit(focusIndex);
  };

  const insertDigits = (index: number, rawValue: string) => {
    const incoming = rawValue.replace(/\D/g, '');
    if (!incoming) {
      const next = [...digits];
      next[index] = '';
      commit(next.join(''), index);
      return;
    }

    const next = [...digits];
    incoming.slice(0, length - index).split('').forEach((digit, offset) => {
      next[index + offset] = digit;
    });
    commit(next.join(''), Math.min(index + incoming.length, length - 1));
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-[12.5px] font-semibold tracking-[0.01em] text-[var(--text-2)]">{label}</span>
        <span aria-hidden="true" className="text-[11px] font-medium tabular-nums text-[var(--text-3)]">
          {normalizedValue.length}/{length}
        </span>
      </div>
      <div
        role="group"
        aria-label={nativeInputLabel ? `${length}-digit code` : label}
        className={cn(
          'grid gap-2 rounded-[14px] border bg-[var(--surface-2)] p-2 transition-[border-color,box-shadow,background-color] duration-200',
          'focus-within:border-[var(--primary)] focus-within:bg-[var(--surface)] focus-within:ring-4 focus-within:ring-[var(--ring)]',
          error ? 'border-[var(--danger)] bg-[var(--danger-bg)] focus-within:border-[var(--danger)] focus-within:ring-[var(--danger-bg)]' : 'border-transparent',
        )}
        style={{ gridTemplateColumns: `repeat(${length}, minmax(0, 1fr))` }}
      >
        {nativeInputLabel && (
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            aria-label={nativeInputLabel}
            value={normalizedValue}
            maxLength={length}
            disabled={disabled}
            tabIndex={-1}
            onChange={(event) => commit(event.target.value)}
            className="sr-only"
          />
        )}
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(element) => { refs.current[index] = element; }}
            aria-label={nativeInputLabel ? `Digit ${index + 1} of ${length}` : `${label} digit ${index + 1}`}
            aria-invalid={error ? true : undefined}
            aria-describedby={(error || hint) ? feedbackId : undefined}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            type="text"
            maxLength={index === 0 ? length : 1}
            value={digit}
            disabled={disabled}
            onFocus={(event) => event.currentTarget.select()}
            onChange={(event) => insertDigits(index, event.target.value)}
            onPaste={(event) => {
              event.preventDefault();
              insertDigits(index, event.clipboardData.getData('text'));
            }}
            onKeyDown={(event) => {
              if (event.key === 'Backspace' && !digit && index > 0) focusDigit(index - 1);
              if (event.key === 'ArrowLeft') {
                event.preventDefault();
                focusDigit(index - 1);
              }
              if (event.key === 'ArrowRight') {
                event.preventDefault();
                focusDigit(index + 1);
              }
            }}
            className={cn(
              'h-12 min-w-0 rounded-[10px] border bg-[var(--surface)] text-center font-mono text-[20px] font-bold tabular-nums text-[var(--text)] shadow-[var(--shadow-1)] outline-none',
              'transition-[transform,border-color,box-shadow,background-color] duration-150 hover:border-[var(--border-strong)]',
              'focus:z-10 focus:-translate-y-0.5 focus:border-[var(--primary)] focus:shadow-[var(--shadow-2)]',
              'disabled:cursor-not-allowed disabled:bg-[var(--surface-3)] disabled:opacity-60',
              error ? 'border-[var(--danger)]' : digit ? 'border-[var(--primary)] bg-[var(--primary-weak)]' : 'border-[var(--border)]',
            )}
          />
        ))}
      </div>
      {(error || hint) && (
        <span id={feedbackId} role={error ? 'alert' : undefined} className={cn('text-[12px] font-medium', error ? 'text-[var(--danger)]' : 'text-[var(--text-3)]')}>
          {error ?? hint}
        </span>
      )}
    </div>
  );
}
